import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { cn } from '@ai-tutor/ui';

/**
 * Three steps of the brand ramp, one per tile, so the three read as one
 * family rather than three white boxes.
 */
const TONE = {
  deep: 'bg-brand-100/70 dark:bg-brand-500/[0.14]',
  mist: 'bg-brand-50/80 dark:bg-brand-500/[0.09]',
  card: 'bg-card/70 dark:bg-card',
} as const;

export type HubTileTone = keyof typeof TONE;

export interface HubTileProps {
  to: string;
  title: string;
  description: ReactNode;
  /** The tile's last line - what clicking does. */
  cta: string;
  /** One quiet line of live status under the description. */
  status?: ReactNode;
  tone?: HubTileTone;
  className?: string;
  /** The tile's live preview, a 96px round: timer ring, breath, count. */
  children: ReactNode;
}

/**
 * One tool on the Wellbeing hub: a round live preview beside what the tool
 * is and what clicking does. Compact on purpose - the content sets the
 * height, so no tile is a tall box with its middle floating. The WHOLE tile
 * is the link: one tab stop, no nested controls.
 */
export function HubTile({
  to,
  title,
  description,
  cta,
  status,
  tone = 'card',
  className,
  children,
}: HubTileProps) {
  return (
    <Link
      to={to}
      className={cn(
        'group flex items-center gap-5 rounded-2xl p-5 backdrop-blur-sm transition-[transform,box-shadow] duration-base',
        'hover:-translate-y-0.5 hover:shadow-elevation-2 active:translate-y-0 motion-reduce:hover:translate-y-0',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        TONE[tone],
        className
      )}
    >
      <div className='min-w-0 flex-1'>
        <h2 className='text-base font-semibold text-foreground'>{title}</h2>
        <p className='mt-0.5 line-clamp-2 text-sm text-muted-foreground'>
          {description}
        </p>
        {status && <div className='mt-2 text-sm'>{status}</div>}
        <span className='mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-foreground'>
          {cta}
          <ArrowRight
            className='h-4 w-4 transition-transform duration-base group-hover:translate-x-0.5 motion-reduce:transition-none'
            aria-hidden='true'
          />
        </span>
      </div>
      {/* Shown first, read last: the link is named by its title, so it is
        "Focus, Pomodoro blocks…" to a screen reader, not "25:00 Ready…". */}
      <div className='order-first flex h-24 w-24 shrink-0 items-center justify-center'>
        {children}
      </div>
    </Link>
  );
}
